"use client";

/* ————— fol. 2r — The text, illuminated (showpiece) —————
   A vellum page holds one verse; as the user scrolls, the apparatus
   blooms in the margin the way a medieval scribe would have built it:
   the word (¹ merimnaō), the place (a prison in Rome), the moment
   (AD 62 on the whole-Bible line) — then the meaning lands.

   Engine: GSAP ScrollTrigger pin + scrub (the engine that survived iOS
   QA in v4; framer-motion useScroll did not — do not convert). Layout
   is a plain CSS grid that never changes during the pin; the timeline
   animates opacity/transform only. No snap (snap+scrub yanks the
   scrollbar). Static stacked fallback for no-JS/reduced-motion:
   everything is visible by default and GSAP hides it on mount.
*/

import { useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";

const VERSE_URL = "/bible-verse-explained/philippians-4-6-7";

export default function IlluminatedVerse() {
  const rootRef = useRef<HTMLElement>(null);
  // false = static fallback layout (meaning sits in normal flow below the
  // grid); true = GSAP owns the stage and the meaning becomes an overlay.
  const [enhanced, setEnhanced] = useState(false);

  useLayoutEffect(() => {
    let cleanup: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled || !rootRef.current) return;
      gsap.registerPlugin(ScrollTrigger);

      const mm = gsap.matchMedia();
      const root = rootRef.current;

      // Desktop: pinned scrub scene. The meaning overlay only exists here.
      const build = () => {
        setEnhanced(true);
        const isDesktop = true;
        const q = gsap.utils.selector(root);

        // Initial states (fallback DOM is fully visible; hide on mount)
        gsap.set(q(".js-margin-card"), { autoAlpha: 0, x: isDesktop ? 36 : 0, y: isDesktop ? 0 : 24 });
        gsap.set(q(".js-card-rule"), { scaleY: 0, transformOrigin: "top center" });
        gsap.set(q(".js-hl"), { scaleX: 0, transformOrigin: "left center" });
        gsap.set(q(".js-fn"), { autoAlpha: 0 });
        gsap.set(q(".js-meaning"), { autoAlpha: 0, y: 30 });
        gsap.set(q(".js-exile"), { autoAlpha: 0, scale: 0.3 });
        gsap.set(q(".js-tline"), { scaleX: 0, transformOrigin: "left center" });

        const tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: q(".js-stage")[0],
            start: "top top",
            end: isDesktop ? "+=300%" : "+=260%",
            pin: true,
            scrub: 0.6,
            anticipatePin: 1,
          },
        });

        // Beat 1 — the page settles (already visible when pin begins)
        tl.fromTo(q(".js-page"), { y: 30, scale: 0.985 }, { y: 0, scale: 1, duration: 0.07 }, 0);

        // Beat 2 — the highlight sweeps "anxious", footnote pops
        tl.to(q(".js-hl"), { scaleX: 1, duration: 0.08 }, 0.1);
        tl.to(q(".js-fn"), { autoAlpha: 1, duration: 0.03 }, 0.17);

        // Beat 3 — ¹ the word
        tl.to(q(".js-card-word .js-card-rule"), { scaleY: 1, duration: 0.05 }, 0.24);
        tl.to(q(".js-card-word"), { autoAlpha: 1, x: 0, y: 0, duration: 0.08 }, 0.26);

        // Beat 4 — ² the place
        tl.to(q(".js-card-place .js-card-rule"), { scaleY: 1, duration: 0.05 }, 0.42);
        tl.to(q(".js-card-place"), { autoAlpha: 1, x: 0, y: 0, duration: 0.08 }, 0.44);

        // Beat 5 — ³ the moment; the timeline draws, the marker ignites
        tl.to(q(".js-card-when .js-card-rule"), { scaleY: 1, duration: 0.05 }, 0.58);
        tl.to(q(".js-card-when"), { autoAlpha: 1, x: 0, y: 0, duration: 0.08 }, 0.6);
        tl.to(q(".js-tline"), { scaleX: 1, duration: 0.08 }, 0.64);
        tl.to(q(".js-exile"), { autoAlpha: 1, scale: 1, duration: 0.04 }, 0.72);

        // Beat 6 — everything recedes; the meaning lands
        tl.to(q(".js-page"), { autoAlpha: 0.07, scale: 0.98, duration: 0.06 }, 0.8);
        tl.to(q(".js-margin-card"), { autoAlpha: 0.05, duration: 0.06 }, 0.8);
        tl.to(q(".js-meaning"), { autoAlpha: 1, y: 0, duration: 0.1 }, 0.84);

        return () => {
          /* mm.revert() restores inline styles */
        };
      };

      mm.add("(min-width: 768px) and (prefers-reduced-motion: no-preference)", () => build());

      // Mobile: the stacked content is taller than the viewport, so pinning
      // would reveal beats below the fold (v3 lesson: no pinned scrub on
      // small screens). Sequential entrances in flow, each SCRUBBED to
      // scroll position (Jay direction 2026-07-10): timeline progress
      // tracks the scrollbar through the element's entry window.
      mm.add("(max-width: 767px) and (prefers-reduced-motion: no-preference)", () => {
        const q = gsap.utils.selector(root);
        const scrubWin = (trigger: Element | undefined) => ({
          trigger,
          start: "top 88%",
          end: "top 45%",
          scrub: 0.5,
        });

        const pageTl = gsap.timeline({
          scrollTrigger: scrubWin(q(".js-page")[0]),
        });
        pageTl
          .fromTo(q(".js-page"), { autoAlpha: 0, y: 28 }, { autoAlpha: 1, y: 0, duration: 0.7, ease: "power3.out" })
          .fromTo(
            q(".js-hl"),
            { scaleX: 0, transformOrigin: "left center" },
            { scaleX: 1, duration: 0.5, ease: "power2.inOut" },
            "-=0.15"
          )
          .fromTo(q(".js-fn"), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 }, "-=0.1");

        q(".js-margin-card").forEach((card) => {
          const rule = card.querySelector(".js-card-rule");
          const cardTl = gsap.timeline({
            scrollTrigger: scrubWin(card),
          });
          cardTl.fromTo(card, { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.6, ease: "power3.out" });
          if (rule) {
            cardTl.fromTo(
              rule,
              { scaleY: 0, transformOrigin: "top center" },
              { scaleY: 1, duration: 0.5, ease: "power2.out" },
              "-=0.2"
            );
          }
          // the whole-Bible strip lives in the "when" card — draw it in
          // the same reversible timeline
          if (card.classList.contains("js-card-when")) {
            const tline = card.querySelector(".js-tline");
            const exile = card.querySelector(".js-exile");
            if (tline) {
              cardTl.fromTo(
                tline,
                { scaleX: 0, transformOrigin: "left center" },
                { scaleX: 1, duration: 0.7, ease: "power2.out" },
                "-=0.2"
              );
            }
            if (exile) {
              cardTl.fromTo(
                exile,
                { autoAlpha: 0, scale: 0.3 },
                { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(2)" },
                "-=0.2"
              );
            }
          }
        });

        gsap.fromTo(
          q(".js-meaning"),
          { autoAlpha: 0, y: 24 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: scrubWin(q(".js-meaning")[0]),
          }
        );
      });

      cleanup = () => mm.revert();
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  return (
    <section
      ref={rootRef}
      data-folio="fol. 2r"
      data-folio-title="The text, illuminated"
      className="relative"
    >
      {/* Section head */}
      <div className="container pt-28 sm:pt-36 pb-8">
        <p className="rubric mb-4">The text, illuminated</p>
        <h2 className="max-w-3xl text-balance">
          One verse, and everything
          <br className="hidden sm:block" /> the margins know about it.
        </h2>
      </div>

      {/* The stage — pinned while the apparatus blooms */}
      <div className="js-stage relative">
        <div className="container relative flex min-h-[100svh] flex-col justify-center py-10">
          <div className="grid gap-6 md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] md:gap-10 lg:gap-14 items-center">
            {/* The vellum page */}
            <div className="js-page reading relative rounded-[4px] border border-reading-border px-7 py-10 sm:px-12 sm:py-14 shadow-[0_60px_120px_-40px_rgba(0,0,0,0.9)]">
              {/* inner rule — the scribe's frame */}
              <div aria-hidden="true" className="pointer-events-none absolute inset-2 border border-[rgba(138,100,32,0.18)] rounded-[2px]" />
              <p className="folio !text-[color:var(--reading-muted)] mb-6">
                ΠΡΟΣ ΦΙΛΙΠΠΗΣΙΟΥΣ · Philippians 4:6, ESV
              </p>
              <p className="font-scripture text-[1.55rem] sm:text-[2.05rem] leading-[1.35] text-reading-foreground">
                Do not be{" "}
                <span className="relative inline-block whitespace-nowrap">
                  <span
                    aria-hidden="true"
                    className="js-hl absolute inset-x-[-0.12em] inset-y-[0.02em] rounded-[2px] bg-[rgba(212,163,62,0.32)]"
                  />
                  <span className="relative">anxious</span>
                  <sup className="js-fn relative text-[0.55em] text-[color:var(--gold-deep)] font-mono ml-0.5">1</sup>
                </span>{" "}
                about anything, but in everything by prayer and supplication with thanksgiving
                let your requests be made known to God.
              </p>
              <div aria-hidden="true" className="mt-8 h-px bg-[rgba(138,100,32,0.45)] max-w-[10rem]" />
              <p className="mt-4 text-sm text-[color:var(--reading-muted)]">
                Written from custody, to a church he founded. See the margin.
              </p>
            </div>

            {/* The margin — apparatus cards */}
            <div className="flex flex-col gap-5 md:gap-6">
              <div className="js-margin-card js-card-word relative pl-5">
                <span aria-hidden="true" className="js-card-rule absolute left-0 top-1 bottom-1 w-px bg-[rgba(212,163,62,0.6)]" />
                <p className="rubric mb-2">¹ The word</p>
                <p className="font-scripture text-2xl text-foreground">
                  μεριμνάω <span className="text-muted-foreground text-lg italic">merimnaō</span>
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1.5 max-w-sm">
                  Care that divides the mind, from <em>merizō</em>, to split into parts. Paul isn&rsquo;t
                  naming a mood. He&rsquo;s naming the pulled-apart feeling.
                </p>
              </div>

              <div className="js-margin-card js-card-place relative pl-5">
                <span aria-hidden="true" className="js-card-rule absolute left-0 top-1 bottom-1 w-px bg-[rgba(212,163,62,0.6)]" />
                <p className="rubric mb-2">² The place</p>
                <svg viewBox="0 0 300 92" className="w-full max-w-[19rem]" aria-hidden="true">
                  <path d="M8,66 C60,26 120,20 168,34 C216,48 258,40 292,22" fill="none" stroke="rgba(212,163,62,0.75)" strokeWidth="1.2" strokeDasharray="3 4" />
                  <circle cx="10" cy="66" r="3" fill="var(--accent)" />
                  <text x="18" y="82" fill="var(--foreground)" fontSize="9" letterSpacing="1.6" opacity="0.9">ROME</text>
                  <circle cx="290" cy="23" r="3" fill="var(--foreground)" opacity="0.9" />
                  <text x="284" y="42" fill="var(--foreground)" fontSize="9" letterSpacing="1.6" textAnchor="end" opacity="0.9">PHILIPPI</text>
                </svg>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1.5 max-w-sm">
                  &ldquo;Do not be anxious,&rdquo; written <span className="text-foreground">from a Roman prison</span>,
                  1,300&nbsp;km from the church he&rsquo;s calming.
                </p>
              </div>

              <div className="js-margin-card js-card-when relative pl-5">
                <span aria-hidden="true" className="js-card-rule absolute left-0 top-1 bottom-1 w-px bg-[rgba(212,163,62,0.6)]" />
                <p className="rubric mb-2">³ The moment</p>
                <div className="relative h-8 max-w-[19rem]">
                  <div className="js-tline absolute left-0 right-0 top-1/2 h-px bg-border-strong" />
                  <span
                    className="js-exile absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full bg-accent shadow-[0_0_16px_rgba(212,163,62,0.8)]"
                    style={{ left: "92%" }}
                  />
                  <span className="folio absolute left-0 top-full mt-1 !text-[0.55rem]">2000 BC</span>
                  <span className="folio absolute right-0 top-full mt-1 !text-[0.55rem] text-right">AD 100</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mt-5 max-w-sm">
                  <span className="text-accent">AD 62.</span> Thirty years after the resurrection, the
                  church still small, its founder in chains, and writing about joy.
                </p>
              </div>
            </div>
          </div>

          {/* The meaning — lands over the receded stage */}
          <div
            className={
              enhanced
                ? "js-meaning pointer-events-none absolute inset-0 z-20 flex items-center justify-center px-5"
                : "js-meaning relative mt-20 flex justify-center px-5"
            }
          >
            {/* soft ink scrim so the landing line owns the frame */}
            <div
              aria-hidden="true"
              className="absolute inset-0"
              style={{ background: "radial-gradient(60rem 30rem at 50% 50%, rgba(13,11,8,0.88), rgba(13,11,8,0.35) 70%, transparent 100%)" }}
            />
            <div className="pointer-events-auto relative max-w-2xl text-center">
              <p className="font-scripture text-[1.7rem] sm:text-4xl leading-snug text-foreground text-balance">
                Paul isn&rsquo;t saying <em>calm down</em>. He&rsquo;s naming the divided mind,
                <span className="text-accent"> and giving every worry one address.</span>
              </p>
              <Link
                href={VERSE_URL}
                className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-foreground border-b border-accent pb-0.5 hover:text-accent transition-colors"
              >
                Read the full breakdown of Philippians 4:6&ndash;7 <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
